import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Icon } from '@shakkerai/components';
import { useDisclosure } from '@mantine/hooks';
import { Popover, Loader } from '@mantine/core';
import cls from 'classnames';
import { mc } from '@shakkerai/report';
import { metaReport } from '@shakkerai/util-meta-report';
import { useTempState, setTempState, getTempState, setProjectState } from '@starflow/app/store';
import { getProjectState, useProjectState, viewportActions } from '@starflow/app/store';
import { ACCEPTED_FILE_EXTS } from '@shakkerai/pro-utils';
import { insertLocalImage } from '@shakkerai/pro-utils';
import { pickLocalFiles } from '@shakkerai/util-file';
import { getElementTools } from '@starflow/app/extension/elements';
import { notifyError } from '@shakkerai/pro-components';
import { translate } from '@shakkerai/util-i18n';
import { Tooltip } from '@mantine/core';
import { toolSampleImages, toolIcons } from '@starflow/app/constants';

const reportLabelMap = {
  inpaint: 'tool.canvas.header.inpaint',
  upscale: 'tool.canvas.header.upscale',
  outpaint: 'tool.canvas.header.outpaint',
  changestyle: 'tool.canvas.header.change_style',
  collage: 'tool.canvas.header.collage',
};

const getSelectionType = (selection) => {
  if (!selection?.length) {
    return 'none';
  }

  if (selection.length === 1 && selection[0]) {
    if (selection[0].startsWith('element-image')) {
      return 'image';
    } else if (selection[0].startsWith('element-group')) {
      return 'group';
    }
  }

  if (selection.every((item) => item.startsWith('element-image'))) {
    return 'images';
  }

  return 'mixed';
};

const getSelectedElements = (selection, elements) => {
  let selected = [];

  if (selection.some((item) => item.startsWith('element'))) {
    selected = selected.concat(
      elements.reduce((result, item) => {
        if (selection.includes(item.id)) {
          result.push(item);
        } else if (item.elements) {
          result = result.concat(
            item.elements.filter((i) => selection.includes(i.id)).map((i) => Object.assign({}, i, { parent: item })),
          );
        }
        return result;
      }, []),
    );
  }

  return selected;
};

const ToolWithPopover = React.memo((props) => {
  const { initialOpened, disabled, tipImage, tools, uploading, onFitView, onClick, onUploadImage } = props;
  const [opened, { close, open }] = useDisclosure(!!initialOpened);
  const isUploading = uploading;

  return (
    <Popover position="bottom" opened={opened || isUploading} offset={{ mainAxis: -10, crossAxis: 0 }}>
      <Popover.Target>
        <div className="relative">
          <button
            onClick={disabled ? undefined : onClick}
            className={`flex h-12 items-center relative top-image-tools-item ${
              disabled && tools.name === 'collage' ? 'opacity-30' : ''
            }`}
            onMouseEnter={open}
            onMouseLeave={close}
          >
            <img src={toolIcons[tools.name]} />
            {tools.label && <span className="m-1 text-xs text-white">{tools.label}</span>}
          </button>
        </div>
      </Popover.Target>
      <Popover.Dropdown
        // top={48}
        onMouseEnter={open}
        onMouseLeave={close}
        className="p-0 bg-transparent border-none shadow-none"
      >
        <div className="relative mt-2 py-[14px] pr-[24px] pl-[12px] rounded-[2px] bg-primary border-none shadow-lg">
          <div className="absolute -top-1 left-1/2 -ml-1 w-2 h-2 bg-primary rotate-45" />
          <span className="text-[14px] text-[#222] font-bold">{translate('imagetool_' + tools.name)}</span>
          <img src={tipImage} className="w-[241px] h-[110px] my-2 bg-black" />
          {!!disabled && tools.name !== 'collage' && (
            <div className="flex items-center">
              <button
                onClick={onFitView}
                className="flex items-center justify-center flex-1 h-6 mr-1 bg-[#edf1f5] rounded-[2px] text-xs text-[#222] font-medium cursor-pointer"
              >
                <Icon className="icon-Navigation mr-0.5" fontSize={12} color="#222" />
                {translate('image_tool_select_button')}
              </button>
              <button
                disabled={uploading}
                onClick={() => onUploadImage(tools)}
                className="flex items-center justify-center flex-1 h-6 ml-1 bg-[#edf1f5] rounded-[2px] text-xs text-[#222] font-medium cursor-pointer"
              >
                {uploading ? (
                  <Loader size={10} className="mr-0.5" color="#222" />
                ) : (
                  <Icon className="icon-proUpload mr-0.5" fontSize={12} color="#222" />
                )}
                {translate('image_tool_new_image_upload')}
              </button>
            </div>
          )}
        </div>
      </Popover.Dropdown>
    </Popover>
  );
});

// https://xingliu.liblib.art/pro?tool=removeSmart
// 通过正则匹配到tool
const refererToolString = location.href.match(/tool=([^&]+)/)?.[1];
const refererToolFixMap = {
  cutout: 'removeBackground',
  removeSmart: 'smartRemove',
};
const refererTool = refererToolFixMap[refererToolString] || refererToolString;

export const ElementTools = React.memo(() => {
  const mode = useTempState('globalMode');
  const isSimple = mode === 'simple';
  const selection = useProjectState('selection');
  const currentTool = useProjectState('currentElementTool');
  const extensionTools = getElementTools();

  const [uploadingTool, setUploadingTool] = useState(null);
  const showRemixSourceImageReplaceTip = useTempState('showRemixSourceImageReplaceTip');

  const selectionType = useMemo(() => {
    return getSelectionType(selection);
  }, [selection]);

  useEffect(() => {
    // 先去掉，这个导致图形工具报错
    // 选中图片之后，如果图片有生成任务，则自动进入对应的聚焦态
    // if (selection?.length === 1 && selection[0]?.startsWith('element-image')) {
    // }
  }, [selection]);

  const handleUploadImage = useCallback(
    async (tool) => {
      if (uploadingTool) return false;

      try {
        const [file] = await pickLocalFiles(ACCEPTED_FILE_EXTS, 1);

        if (!file) return false;

        setUploadingTool(tool.name);

        const elementId = await insertLocalImage(file, { position: 'same-as-gallery', from: tool.name });
        const currentElementTool = getTempState('currentElementTool');

        if (currentElementTool && currentElementTool !== tool.name) return false;

        setProjectState({ selection: [elementId] });
        handleClickTool(tool, false);
        setUploadingTool(null);
        return true;
      } catch (error) {
        console.log(error);
        notifyError(translate('upload_fail_toast'), error.message || error.msg || error);
        setUploadingTool(null);
        return false;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [uploadingTool],
  );

  const handleFitView = useCallback(() => {
    viewportActions.fitView();
  }, []);

  const handleClickTool = useCallback((tool) => {
    const selection = getProjectState('selection');

    if (tool.name === 'removeBackground') {
      mc('tool.canvas.header.remove_background');
    }

    if (tool.name === 'crop') {
      mc('tool.canvas.header.resize');
    }

    if (tool.name === 'removeSmart') {
      mc('tool.canvas.header.erase');
    }

    if (tool.name === 'selectAreaTool') {
      mc('tool.canvas.header.copy');
    }

    metaReport(`generateBtn`);

    if (tool.handler) {
      tool.handler({ selection });
    } else {
      if (selection.length !== 1) return;

      setProjectState({
        currentNodeId: null,
        editingElementId: selection[0],
        currentElementTool: tool.name,
      });
    }
  }, []);

  const disabled = selection.length !== 1 || !selection[0]?.startsWith('element-image');

  const ToolItemComp = (item, index) => {
    return (
      <div className="mr-6" key={`${item.name}-${index}`}>
        <ToolWithPopover
          tools={item}
          disabled={disabled}
          key={`${item.name}-${index}`}
          tipImage={toolSampleImages[item.name]}
          uploading={uploadingTool === item.name}
          selectionType={selectionType}
          initialOpened={refererTool === item.name}
          onFitView={handleFitView}
          onUploadImage={handleUploadImage}
          onClick={() => handleClickTool(item)}
        />
      </div>
    );
  };

  const TopTools = useMemo(() => {
    const AIImageTools = extensionTools?.filter((tools) => tools.isAI);
    const simpleImageTools = extensionTools?.filter((tools) => !tools.isAI);

    return (
      <div
        className={cls(
          'flex items-center justify-center w-full',
          `${disabled && selection.length > 0 ? 'pointer-events-none' : ''}`,
        )}
      >
        {AIImageTools?.map((item, index) => {
          return ToolItemComp(item, index);
        })}
        <div className="h-[14px] w-[1px] mr-6 bg-[#fff] opacity-40"></div>
        {simpleImageTools?.map((item, index) => {
          if (item.icon) {
            return (
              <Tooltip withArrow position="top" key={`${item.name}-${index}`} className="scale-90" label={item.label}>
                <button data-name={item.name} onClick={() => handleClickTool(item)}>
                  <Icon fontSize={18} className={`cursor-pointer ${item.icon}`} />
                </button>
              </Tooltip>
            );
          }
          return ToolItemComp(item, index);
        })}
      </div>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, currentTool, selectionType, isSimple, showRemixSourceImageReplaceTip, uploadingTool, disabled]);

  useEffect(() => {
    if (currentTool && reportLabelMap[currentTool]) {
      mc(reportLabelMap[currentTool], {
        mode: getTempState('globalMode'),
      });
    }
  }, [currentTool]);

  return (
    <div
      className={cls(
        'top-image-tools-wrap',
        `${disabled && selection?.length > 0 ? 'cursor-not-allowed brightness-50' : ''}`,
      )}
    >
      {TopTools}
    </div>
  );
});
